import { useState, useEffect } from "react";

const NAVY = "#18365E";
const CORAL = "#FF6E4C";
const BG = "#F5F7FA";
const BORDER = "#e5e7eb";
const MUTED = "#717182";
const WHITE = "#ffffff";
const AMBER = "#F59E0B";
const GREEN = "#10B981";
const NAVY_LIGHT = "#2a4f7a";
const TEAL = "#0D9488";

const useFadeIn = (d = 0) => {
  const [v, setV] = useState(false);
  useEffect(() => { const t = setTimeout(() => setV(true), d); return () => clearTimeout(t); }, [d]);
  return { opacity: v ? 1 : 0, transform: v ? "translateY(0)" : "translateY(14px)", transition: `opacity 0.7s ease ${d}ms, transform 0.7s ease ${d}ms` };
};

const pW = [0.18, 0.27, 0.28, 0.27];
const pS = pW.reduce((a, w, i) => { a.push(i === 0 ? 0 : a[i-1] + pW[i-1]); return a; }, []);
const pC = [CORAL, AMBER, NAVY_LIGHT, NAVY];
const pN = ["Proof & Survival", "Early Penetration", "Platform Pull", "Category Leadership"];
const pTshort = ["0–6 mo", "6–18 mo", "18–36 mo", "36+ mo"];

const allMods = [
  [{ n: "PSI", sub: "Entry Point", c: CORAL }, { n: "DVA", sub: "Core Engine", c: NAVY }],
  [{ n: "PSI", sub: "Scaling", c: CORAL }, { n: "DVA", sub: "Expansion", c: NAVY }],
  [{ n: "MSI", sub: "Market Intel", c: TEAL }, { n: "CSI", sub: "Contract Intel", c: NAVY_LIGHT }],
  [{ n: "SPI", sub: "Closed Loop", c: GREEN }, { n: "DE", sub: "New Industries", c: NAVY }],
];

const allOutcomes = [
  ["Close 2–3 lighthouse pilots", "Generate services revenue", "Lock wedge positioning"],
  ["5–10 enterprise contracts", "PSI self-serve launch", "Early channel partners"],
  ["Product-led revenue majority", "MSI + CSI modules live", "$10M+ ARR trajectory"],
  ["Cross-industry expansion", "Closed-loop intelligence", "$50M+ ARR path"],
];


// ═══════════════════════════════════════════════════════════════
// DETAIL VIEW — viewBox 700 wide → renders at ~1.47x in 1030px
// All text 7px+ SVG = 10px+ rendered. No micro-text.
// ═══════════════════════════════════════════════════════════════
function DetailTimeline() {
  const W = 700, H = 358;
  const ML = 16, MR = 8;
  const cx = (pct) => ML + pct * (W - ML - MR);

  const PH = 14, PH_H = 22;
  const T1 = 40;       // timing
  const T2 = 62;       // modules
  const T3 = 150;      // monetization
  const T4 = 200;      // growth
  const T5 = 240;      // outcomes
  const BL = 298;      // thesis

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block" }}>
      <defs>
        <linearGradient id="rg" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor={CORAL} stopOpacity="1" /><stop offset="100%" stopColor={CORAL} stopOpacity="0.55" /></linearGradient>
        <linearGradient id="cg" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor={AMBER} stopOpacity="0.5" /><stop offset="60%" stopColor={NAVY_LIGHT} stopOpacity="0.35" /><stop offset="100%" stopColor={NAVY} stopOpacity="0.15" /></linearGradient>
        <linearGradient id="ag" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor={CORAL} /><stop offset="100%" stopColor={NAVY} /></linearGradient>
        <filter id="gl"><feGaussianBlur stdDeviation="2" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge></filter>
        {pC.map((c, i) => (<linearGradient key={i} id={`pb${i}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={c} stopOpacity="0.05" /><stop offset="100%" stopColor={c} stopOpacity="0.012" /></linearGradient>))}
      </defs>

      {/* Divider line */}
      <line x1={cx(0)} y1={2} x2={cx(1)} y2={2} stroke={BORDER} strokeWidth="0.6" />

      {/* PHASE COLUMNS */}
      {pS.map((s, i) => {
        const x1 = cx(s), x2 = cx(s + pW[i]), w = x2 - x1;
        return (
          <g key={i}>
            <rect x={x1} y={PH} width={w} height={BL - PH} fill={`url(#pb${i})`} />
            {i > 0 && <line x1={x1} y1={PH} x2={x1} y2={BL} stroke={BORDER} strokeWidth="0.7" strokeDasharray="4,3" opacity="0.5" />}
            <rect x={x1} y={PH} width={w} height={PH_H} fill={pC[i]} opacity="0.08" />
            <rect x={x1} y={PH} width={2.5} height={PH_H} fill={pC[i]} />
            <circle cx={x1 + 12} cy={PH + 11} r={7} fill={pC[i]} />
            <text x={x1 + 12} y={PH + 14.5} textAnchor="middle" fontSize="8" fontWeight="700" fill={WHITE} fontFamily="Montserrat">{i + 1}</text>
            <text x={x1 + 24} y={PH + 10} fontSize="9" fontWeight="700" fill={NAVY} fontFamily="Montserrat">{pN[i]}</text>
            <text x={x1 + 24} y={PH + 20} fontSize="7" fontWeight="600" fill={MUTED} fontFamily="Montserrat">{pTshort[i]}</text>
          </g>
        );
      })}

      {/* TIMING */}
      <rect x={cx(0)} y={T1} width={cx(1) - cx(0)} height={13} rx={3} fill={`${NAVY}06`} />
      <rect x={cx(0)} y={T1} width={cx(pW[0]) - cx(0)} height={13} rx={3} fill="url(#rg)" />
      <text x={cx(0) + (cx(pW[0]) - cx(0)) / 2} y={T1 + 9.5} textAnchor="middle" fontSize="7" fontWeight="700" fill={WHITE} fontFamily="Montserrat">5-MO RUNWAY</text>
      <rect x={cx(pW[0]) + 2} y={T1} width={cx(pS[3]) - cx(pW[0]) - 2} height={13} rx={3} fill="url(#cg)" />
      <text x={cx(pW[0]) + (cx(pS[3]) - cx(pW[0])) / 2} y={T1 + 9.5} textAnchor="middle" fontSize="7" fontWeight="600" fill={NAVY} fontFamily="Montserrat" opacity="0.5">12–36 MO CATEGORY DEVELOPMENT</text>
      <circle cx={cx(0.015)} cy={T1 + 6.5} r={4.5} fill={CORAL} filter="url(#gl)" /><circle cx={cx(0.015)} cy={T1 + 6.5} r={2.2} fill={WHITE} />

      {/* Track dividers */}
      {[T2 - 2, T3 - 4, T4 - 4, T5 - 4, BL].map((y, i) => (<line key={i} x1={cx(0)} y1={y} x2={cx(1)} y2={y} stroke={BORDER} strokeWidth="0.4" />))}

      {/* MODULES */}
      {pS.map((s, i) => {
        const x1 = cx(s), x2 = cx(s + pW[i]), ctr = (x1 + x2) / 2;
        const ms = allMods[i];
        const cW = i === 0 ? 48 : 54, cH = 36, gap = 4;
        const totalW = ms.length * cW + (ms.length - 1) * gap;
        const startX = ctr - totalW / 2;
        return (
          <g key={`m${i}`}>
            {ms.map((m, j) => {
              const mx = startX + j * (cW + gap);
              return (
                <g key={j}>
                  <rect x={mx} y={T2 + 2} width={cW} height={cH} rx={5} fill={WHITE} stroke={m.c} strokeWidth="1.1" />
                  <rect x={mx} y={T2 + 2} width={cW} height={14} rx="5 5 0 0" fill={m.c} opacity="0.06" />
                  <rect x={mx} y={T2 + 2} width={2.5} height={cH} rx="1.25 0 0 1.25" fill={m.c} />
                  <text x={mx + cW / 2} y={T2 + 14} textAnchor="middle" fontSize="10" fontWeight="800" fill={m.c} fontFamily="Montserrat">{m.n}</text>
                  <text x={mx + cW / 2} y={T2 + 28} textAnchor="middle" fontSize="7" fontWeight="500" fill={MUTED} fontFamily="Montserrat">{m.sub}</text>
                </g>
              );
            })}
          </g>
        );
      })}
      {/* Connection arrows */}
      {[0, 1, 2].map(i => { const x1 = cx(pS[i] + pW[i]) - 3, x2 = cx(pS[i + 1]) + 3, cy = T2 + 20; return <g key={`c${i}`} opacity="0.18"><line x1={x1} y1={cy} x2={x2 - 4} y2={cy} stroke={NAVY} strokeWidth="1.2" /><polygon points={`${x2 - 4},${cy - 2} ${x2},${cy} ${x2 - 4},${cy + 2}`} fill={NAVY} /></g>; })}

      {/* Compounds arrow */}
      <g opacity="0.25"><line x1={cx(0.05)} y1={T2 + 56} x2={cx(0.95)} y2={T2 + 56} stroke="url(#ag)" strokeWidth="1" /><polygon points={`${cx(0.95)},${T2 + 54} ${cx(0.95) + 4},${T2 + 56} ${cx(0.95)},${T2 + 58}`} fill={NAVY} /></g>
      <text x={cx(0.5)} y={T2 + 66} textAnchor="middle" fontSize="7" fontWeight="600" fill={MUTED} fontFamily="Montserrat" fontStyle="italic" opacity="0.6">Intelligence layers compound over time</text>

      {/* MONETIZATION */}
      {pS.map((s, i) => {
        const x1 = cx(s) + 2, x2 = cx(s + pW[i]) - 2, w = x2 - x1;
        const labels = ["Services", "Hybrid", "Product-Led", "Platform"];
        const subs = ["The bridge", "Services + Product", "Revenue majority", "85–90% margins"];
        return (
          <g key={`mo${i}`}>
            <rect x={x1} y={T3} width={w} height={24} rx={4} fill={pC[i]} opacity="0.07" />
            <rect x={x1} y={T3} width={2.5} height={24} rx="1.25 0 0 1.25" fill={pC[i]} opacity="0.4" />
            <text x={x1 + w / 2} y={T3 + 10} textAnchor="middle" fontSize="8.5" fontWeight="700" fill={pC[i]} fontFamily="Montserrat">{labels[i]}</text>
            <text x={x1 + w / 2} y={T3 + 20} textAnchor="middle" fontSize="7" fontWeight="500" fill={MUTED} fontFamily="Montserrat">{subs[i]}</text>
          </g>
        );
      })}
      <text x={cx(0.5)} y={T3 + 36} textAnchor="middle" fontSize="7" fontWeight="600" fontStyle="italic" fill={MUTED} fontFamily="Montserrat" opacity="0.6">Services are not the business — services are the bridge to the business</text>

      {/* GROWTH */}
      {[{ phase: 0, label: "Product-Led (PSI)", c: CORAL }, { phase: 0, label: "Enterprise (DVA)", c: NAVY }, { phase: 1, label: "Channel (Consultancies)", c: NAVY_LIGHT }, { phase: 3, label: "Domain Expansion", c: MUTED }].map((v, i) => {
        const x1 = cx(pS[v.phase]), x2 = cx(1) - 3, y = T4 + i * 8;
        const fillW = [1, 0.75, 0.55, 0.25][i];
        return (
          <g key={`v${i}`} opacity={i === 3 ? 0.25 : 0.5}>
            <rect x={x1} y={y} width={x2 - x1} height={4} rx={2} fill={v.c} opacity="0.1" />
            <rect x={x1} y={y} width={(x2 - x1) * fillW} height={4} rx={2} fill={v.c} opacity="0.4" />
            <circle cx={x1 - 1} cy={y + 2} r={2.2} fill={v.c} opacity="0.5" />
            <text x={x1 + 7} y={y + 4} fontSize="5.5" fontWeight="700" fill={v.c} fontFamily="Montserrat">{v.label}</text>
          </g>
        );
      })}

      {/* OUTCOMES */}
      {pS.map((s, i) => { const x1 = cx(s) + 5; return (<g key={`o${i}`}>{allOutcomes[i].map((o, j) => (<g key={j}><circle cx={x1 + 2} cy={T5 + 2 + j * 10 + 2} r={1.5} fill={pC[i]} opacity="0.4" /><text x={x1 + 7} y={T5 + 2 + j * 10 + 5} fontSize="7" fontWeight="500" fill={NAVY} fontFamily="Montserrat" opacity="0.7">{o}</text></g>))}</g>); })}

      <line x1={cx(0)} y1={BL} x2={cx(1)} y2={BL} stroke={BORDER} strokeWidth="0.4" />

      {/* THESIS */}
      {[{ x: cx(0.18), l: "PSI", v: "creates entry", c: CORAL }, { x: cx(0.46), l: "DVA", v: "creates value", c: NAVY }, { x: cx(0.74), l: "Platform", v: "creates scale", c: NAVY_LIGHT }].map((item, i) => (
        <g key={i}><rect x={item.x - 32} y={BL + 6} width={64} height={20} rx={5} fill={item.c} opacity="0.06" stroke={item.c} strokeWidth="0.8" strokeOpacity="0.15" /><text x={item.x} y={BL + 16} textAnchor="middle" fontSize="9" fontWeight="800" fill={item.c} fontFamily="Montserrat">{item.l}</text><text x={item.x} y={BL + 24} textAnchor="middle" fontSize="7" fontWeight="500" fill={MUTED} fontFamily="Montserrat">{item.v}</text></g>
      ))}
      {[0, 1].map(i => { const xs = [cx(0.18) + 32, cx(0.46) + 32], xe = [cx(0.46) - 32, cx(0.74) - 32]; return <g key={i} opacity="0.2"><line x1={xs[i] + 2} y1={BL + 16} x2={xe[i] - 3} y2={BL + 16} stroke={NAVY} strokeWidth="1" /><polygon points={`${xe[i] - 3},${BL + 14} ${xe[i]},${BL + 16} ${xe[i] - 3},${BL + 18}`} fill={NAVY} /></g>; })}

      <rect x={cx(0)} y={PH} width={cx(pW[0]) - cx(0)} height={BL - PH} fill={CORAL} opacity="0.012"><animate attributeName="opacity" values="0.012;0.025;0.012" dur="3s" repeatCount="indefinite" /></rect>
    </svg>
  );
}


// ═══════════════════════════════════════════════════════════════
// SLIDE VIEW — 960x490, 10px gap below title, min font 8px
// Removed all micro-labels. Clean, readable.
// ═══════════════════════════════════════════════════════════════
function SlideTimeline() {
  const W = 960, H = 490;
  const ML = 14, MR = 14;
  const cx = (pct) => ML + pct * (W - ML - MR);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block", background: WHITE, borderRadius: 10 }}>
      <defs>
        <linearGradient id="srg" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor={CORAL} stopOpacity="1" /><stop offset="100%" stopColor={CORAL} stopOpacity="0.6" /></linearGradient>
        <linearGradient id="scg" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor={AMBER} stopOpacity="0.5" /><stop offset="60%" stopColor={NAVY_LIGHT} stopOpacity="0.3" /><stop offset="100%" stopColor={NAVY} stopOpacity="0.1" /></linearGradient>
        <linearGradient id="sag" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor={CORAL} /><stop offset="100%" stopColor={NAVY} /></linearGradient>
        {pC.map((c, i) => (<linearGradient key={i} id={`spb${i}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={c} stopOpacity="0.04" /><stop offset="100%" stopColor={c} stopOpacity="0.01" /></linearGradient>))}
      </defs>

      <rect width={W} height={H} fill={WHITE} rx={10} />

      {/* TITLE BAR — 44px tall */}
      <rect x={0} y={0} width={W} height={44} fill={NAVY} opacity="0.025" />
      <rect x={20} y={10} width={4} height={24} rx={2} fill={CORAL} />
      <text x={32} y={22} fontSize="9" fontWeight="700" fill={CORAL} fontFamily="Montserrat" letterSpacing="0.1em">STRATEGIC ROADMAP</text>
      <text x={32} y={38} fontSize="18" fontWeight="800" fill={NAVY} fontFamily="Montserrat">From Proof to Platform</text>
      {/* Badges */}
      <rect x={W - 326} y={10} width={140} height={24} rx={6} fill={CORAL} opacity="0.08" stroke={CORAL} strokeWidth="1" strokeOpacity="0.18" />
      <circle cx={W - 314} cy={22} r={3.5} fill={CORAL}><animate attributeName="opacity" values="1;0.4;1" dur="2s" repeatCount="indefinite" /></circle>
      <text x={W - 304} y={26} fontSize="9" fontWeight="700" fill={CORAL} fontFamily="Montserrat">5-MO RUNWAY</text>
      <rect x={W - 178} y={10} width={162} height={24} rx={6} fill={NAVY} opacity="0.04" stroke={NAVY} strokeWidth="1" strokeOpacity="0.1" />
      <text x={W - 166} y={26} fontSize="8.5" fontWeight="600" fill={NAVY} fontFamily="Montserrat">12–36 MO CATEGORY DEV</text>

      {/* Divider line below title */}
      <line x1={20} y1={44} x2={W - 20} y2={44} stroke={BORDER} strokeWidth="0.6" />

      {/* PHASE HEADERS — start at y=58 (14px gap below divider) */}
      {pS.map((s, i) => {
        const x1 = cx(s), x2 = cx(s + pW[i]), w = x2 - x1;
        return (
          <g key={i}>
            <rect x={x1} y={58} width={w} height={394} fill={`url(#spb${i})`} />
            {i > 0 && <line x1={x1} y1={58} x2={x1} y2={452} stroke={BORDER} strokeWidth="1" strokeDasharray="4,4" opacity="0.35" />}
            <rect x={x1} y={58} width={w} height={34} fill={pC[i]} opacity="0.06" />
            <rect x={x1} y={58} width={w} height={3} fill={pC[i]} opacity="0.5" />
            <circle cx={x1 + 18} cy={78} r={10} fill={pC[i]} />
            <text x={x1 + 18} y={82} textAnchor="middle" fontSize="11" fontWeight="800" fill={WHITE} fontFamily="Montserrat">{i + 1}</text>
            <text x={x1 + 34} y={76} fontSize="13" fontWeight="700" fill={NAVY} fontFamily="Montserrat">{pN[i]}</text>
            <text x={x1 + 34} y={88} fontSize="9" fontWeight="600" fill={MUTED} fontFamily="Montserrat">{pTshort[i]}</text>
          </g>
        );
      })}

      {/* TIMING BAR */}
      <rect x={cx(0)} y={98} width={cx(1) - cx(0)} height={16} rx={4} fill={`${NAVY}05`} />
      <rect x={cx(0)} y={98} width={cx(pW[0]) - cx(0)} height={16} rx={4} fill="url(#srg)" />
      <text x={cx(0) + (cx(pW[0]) - cx(0)) / 2} y={109} textAnchor="middle" fontSize="8" fontWeight="700" fill={WHITE} fontFamily="Montserrat">RUNWAY</text>
      <rect x={cx(pW[0]) + 2} y={98} width={cx(pS[3]) - cx(pW[0]) - 2} height={16} rx={4} fill="url(#scg)" />
      <text x={cx(pW[0]) + (cx(pS[3]) - cx(pW[0])) / 2} y={109} textAnchor="middle" fontSize="8" fontWeight="600" fill={NAVY} fontFamily="Montserrat" opacity="0.4">CATEGORY DEVELOPMENT</text>

      {/* MODULE CARDS — y=124 */}
      {pS.map((s, i) => {
        const x1 = cx(s), x2 = cx(s + pW[i]), ctr = (x1 + x2) / 2;
        const ms = allMods[i];
        const cW = i === 0 ? 66 : 76, cH = 48, gap = 6;
        const total = ms.length * cW + gap;
        const sx = ctr - total / 2;
        return (
          <g key={`sm${i}`}>
            {ms.map((m, j) => {
              const mx = sx + j * (cW + gap);
              return (
                <g key={j}>
                  <rect x={mx} y={124} width={cW} height={cH} rx={7} fill={WHITE} stroke={m.c} strokeWidth="1.3" />
                  <rect x={mx} y={124} width={cW} height={18} rx="7 7 0 0" fill={m.c} opacity="0.06" />
                  <rect x={mx} y={124} width={3.5} height={cH} rx="1.75 0 0 1.75" fill={m.c} />
                  <text x={mx + cW / 2} y={139} textAnchor="middle" fontSize="14" fontWeight="800" fill={m.c} fontFamily="Montserrat">{m.n}</text>
                  <text x={mx + cW / 2} y={158} textAnchor="middle" fontSize="8" fontWeight="500" fill={MUTED} fontFamily="Montserrat">{m.sub}</text>
                </g>
              );
            })}
          </g>
        );
      })}
      {[0, 1, 2].map(i => { const x1 = cx(pS[i] + pW[i]) - 5, x2 = cx(pS[i + 1]) + 5, cy = 148; return <g key={`sc${i}`} opacity="0.15"><line x1={x1} y1={cy} x2={x2 - 5} y2={cy} stroke={NAVY} strokeWidth="1.5" /><polygon points={`${x2 - 5},${cy - 3} ${x2},${cy} ${x2 - 5},${cy + 3}`} fill={NAVY} /></g>; })}

      {/* OUTCOMES */}
      {pS.map((s, i) => {
        const x1 = cx(s) + 8;
        return (<g key={`so${i}`}>{allOutcomes[i].slice(0, 2).map((o, j) => (<g key={j}><circle cx={x1 + 3} cy={184 + j * 16} r={2.5} fill={pC[i]} opacity="0.35" /><text x={x1 + 11} y={188 + j * 16} fontSize="9" fontWeight="500" fill={NAVY} fontFamily="Montserrat" opacity="0.65">{o}</text></g>))}</g>);
      })}

      {/* DIVIDER */}
      <line x1={cx(0)} y1={224} x2={cx(1)} y2={224} stroke={BORDER} strokeWidth="0.5" />

      {/* MONETIZATION */}
      {pS.map((s, i) => {
        const x1 = cx(s) + 3, x2 = cx(s + pW[i]) - 3, w = x2 - x1;
        const labels = ["Services", "Hybrid", "Product-Led", "Platform"];
        const subs = ["The bridge", "Services + Product", "Revenue majority", "85–90% margins"];
        return (
          <g key={`smo${i}`}>
            <rect x={x1} y={234} width={w} height={34} rx={7} fill={pC[i]} opacity="0.06" />
            <rect x={x1} y={234} width={3.5} height={34} rx="1.75 0 0 1.75" fill={pC[i]} opacity="0.4" />
            <text x={x1 + w / 2} y={251} textAnchor="middle" fontSize="12" fontWeight="700" fill={pC[i]} fontFamily="Montserrat">{labels[i]}</text>
            <text x={x1 + w / 2} y={264} textAnchor="middle" fontSize="8" fontWeight="500" fill={MUTED} fontFamily="Montserrat">{subs[i]}</text>
          </g>
        );
      })}
      <text x={cx(0.5)} y={286} textAnchor="middle" fontSize="9" fontWeight="600" fontStyle="italic" fill={MUTED} fontFamily="Montserrat">Services are not the business — services are the bridge to the business</text>

      {/* DIVIDER */}
      <line x1={cx(0)} y1={298} x2={cx(1)} y2={298} stroke={BORDER} strokeWidth="0.5" />

      {/* GROWTH VECTORS */}
      {[
        { phase: 0, label: "Product-Led (PSI)", c: CORAL },
        { phase: 0, label: "Enterprise (DVA)", c: NAVY },
        { phase: 1, label: "Channel", c: NAVY_LIGHT },
        { phase: 3, label: "Domain Expansion", c: MUTED },
      ].map((v, i) => {
        const x1 = cx(pS[v.phase]), x2 = cx(1) - 4, y = 308 + i * 13;
        const fillW = [1, 0.75, 0.55, 0.25][i];
        return (
          <g key={`sv${i}`} opacity={i === 3 ? 0.25 : 0.5}>
            <rect x={x1} y={y} width={x2 - x1} height={6} rx={3} fill={v.c} opacity="0.1" />
            <rect x={x1} y={y} width={(x2 - x1) * fillW} height={6} rx={3} fill={v.c} opacity="0.4" />
            <circle cx={x1 - 1} cy={y + 3} r={3} fill={v.c} opacity="0.5" />
            <text x={x1 + 10} y={y + 6} fontSize="8" fontWeight="700" fill={v.c} fontFamily="Montserrat">{v.label}</text>
          </g>
        );
      })}

      {/* THESIS */}
      <line x1={cx(0)} y1={364} x2={cx(1)} y2={364} stroke={BORDER} strokeWidth="0.5" />
      {[
        { x: cx(0.18), l: "PSI", v: "creates entry", c: CORAL },
        { x: cx(0.46), l: "DVA", v: "creates value", c: NAVY },
        { x: cx(0.76), l: "Platform", v: "creates scale", c: NAVY_LIGHT },
      ].map((item, i) => (
        <g key={`st${i}`}>
          <rect x={item.x - 52} y={372} width={104} height={32} rx={8} fill={item.c} opacity="0.05" stroke={item.c} strokeWidth="1.2" strokeOpacity="0.15" />
          <text x={item.x} y={388} textAnchor="middle" fontSize="15" fontWeight="800" fill={item.c} fontFamily="Montserrat">{item.l}</text>
          <text x={item.x} y={400} textAnchor="middle" fontSize="9" fontWeight="500" fill={MUTED} fontFamily="Montserrat">{item.v}</text>
        </g>
      ))}
      {[0, 1].map(i => { const xs = [cx(0.18) + 52, cx(0.46) + 52], xe = [cx(0.46) - 52, cx(0.76) - 52]; return <g key={`sa${i}`} opacity="0.2"><line x1={xs[i] + 3} y1={388} x2={xe[i] - 5} y2={388} stroke={NAVY} strokeWidth="1.5" /><polygon points={`${xe[i] - 5},${385} ${xe[i]},${388} ${xe[i] - 5},${391}`} fill={NAVY} /></g>; })}

      {/* FOOTER */}
      <line x1={20} y1={422} x2={W - 20} y2={422} stroke={BORDER} strokeWidth="0.4" />
      <text x={20} y={438} fontSize="8" fontWeight="600" fill={MUTED} fontFamily="Montserrat" letterSpacing="0.04em" opacity="0.45">ADAPTIVE PRODUCT · STRATEGY INTELLIGENCE PLATFORM · CONFIDENTIAL</text>
      <text x={W - 20} y={438} textAnchor="end" fontSize="8" fontWeight="500" fill={MUTED} fontFamily="Montserrat" fontStyle="italic" opacity="0.45">"Replace Opinion with Evidence — in the Decisions That Matter Most."</text>

      {/* Phase 1 wash */}
      <rect x={cx(0)} y={58} width={cx(pW[0]) - cx(0)} height={394} fill={CORAL} opacity="0.01"><animate attributeName="opacity" values="0.01;0.02;0.01" dur="3s" repeatCount="indefinite" /></rect>
    </svg>
  );
}


// ═══════════════════════════════════════════════════════════════
// SUPPORTING
// ═══════════════════════════════════════════════════════════════
function VCard({ icon, label, sub, desc, phase, color }) {
  return (
    <div style={{ flex: 1, borderRadius: 10, border: `1px solid ${BORDER}`, padding: "14px 13px", background: WHITE }}>
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 7, color }}>{icon}<span style={{ fontSize: 12, fontWeight: 700 }}>{label}</span></div>
      <div style={{ fontSize: 11.5, fontWeight: 600, color: NAVY, marginBottom: 3 }}>{sub}</div>
      <div style={{ fontSize: 10.5, color: MUTED, lineHeight: 1.4, marginBottom: 6 }}>{desc}</div>
      <span style={{ fontSize: 9, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.06em", opacity: 0.6 }}>{phase}</span>
    </div>
  );
}

const ic = {
  pl: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1" y="1" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" /><path d="M5 4l4 3-4 3V4z" fill="currentColor" /></svg>,
  ent: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="2" y="4" width="10" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" /><rect x="5" y="1" width="4" height="3" rx="0.5" stroke="currentColor" strokeWidth="1.5" /><line x1="5" y1="7" x2="9" y2="7" stroke="currentColor" strokeWidth="1.2" /><line x1="5" y1="10" x2="9" y2="10" stroke="currentColor" strokeWidth="1.2" /></svg>,
  ch: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="3" r="2" stroke="currentColor" strokeWidth="1.5" /><circle cx="3" cy="11" r="2" stroke="currentColor" strokeWidth="1.5" /><circle cx="11" cy="11" r="2" stroke="currentColor" strokeWidth="1.5" /><line x1="7" y1="5" x2="3" y2="9" stroke="currentColor" strokeWidth="1.2" /><line x1="7" y1="5" x2="11" y2="9" stroke="currentColor" strokeWidth="1.2" /></svg>,
  dom: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1.5" /><ellipse cx="7" cy="7" rx="2.5" ry="5.5" stroke="currentColor" strokeWidth="1.2" /><line x1="1.5" y1="7" x2="12.5" y2="7" stroke="currentColor" strokeWidth="1.2" /></svg>,
};


// ═══════════════════════════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════════════════════════
export default function SIPStrategicRoadmap() {
  const [view, setView] = useState("detail");
  const f0 = useFadeIn(80);
  const f1 = useFadeIn(200);
  const f2 = useFadeIn(500);
  const f3 = useFadeIn(700);

  return (
    <div style={{ fontFamily: "'Montserrat', sans-serif", background: BG, minHeight: "100vh" }}>
      <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      <style>{`
        @keyframes pulse{0%,100%{opacity:1}50%{opacity:0.4}}
        @media print {
          @page { size: landscape; margin: 10mm 8mm; }
          body, html { background: #fff !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .no-print { display: none !important; }
          * { animation: none !important; transition: none !important; }
          .print-section { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>

      <div style={{ maxWidth: view === "slide" ? 1160 : 1100, margin: "0 auto", padding: view === "slide" ? "16px 24px 20px" : "32px 24px 40px", transition: "all 0.3s ease" }}>

        {/* HEADER */}
        <div style={f0}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: view === "detail" ? 6 : 0 }}>
                <div style={{ width: 4, height: view === "detail" ? 26 : 20, background: CORAL, borderRadius: 2 }} />
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: CORAL }}>Strategic Roadmap</span>
                <span style={{ fontSize: 11, color: MUTED }}>·</span>
                <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: NAVY }}>Strategy Intelligence Platform</span>
              </div>
              {view === "detail" && (
                <>
                  <h1 style={{ fontSize: 28, fontWeight: 800, color: NAVY, margin: "0 0 3px", letterSpacing: "-0.01em", lineHeight: 1.15 }}>From Proof to Platform</h1>
                  <p style={{ fontSize: 13, color: MUTED, margin: 0 }}>How Adaptive Product wins from today to category leadership</p>
                </>
              )}
            </div>
            <div className="no-print" style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0, marginTop: 2 }}>
              <div style={{ display: "flex", borderRadius: 8, overflow: "hidden", border: `1px solid ${BORDER}`, background: WHITE }}>
                {[{ key: "detail", label: "Detail View" }, { key: "slide", label: "Slide View" }].map((tab) => (
                  <button key={tab.key} onClick={() => setView(tab.key)} style={{
                    padding: "6px 14px", border: "none", cursor: "pointer",
                    fontSize: 11.5, fontWeight: 600, fontFamily: "Montserrat",
                    background: view === tab.key ? NAVY : WHITE,
                    color: view === tab.key ? WHITE : NAVY,
                    transition: "all 0.2s ease",
                  }}>{tab.label}</button>
                ))}
              </div>
              <button onClick={() => window.print()} style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "6px 14px", borderRadius: 8,
                background: NAVY, color: WHITE, border: "none",
                fontSize: 11.5, fontWeight: 600, fontFamily: "Montserrat",
                cursor: "pointer", boxShadow: "0 2px 8px rgba(24,54,94,0.2)",
              }}
                onMouseEnter={(e) => { e.currentTarget.style.background = NAVY_LIGHT; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = NAVY; }}
              >
                <svg width="14" height="14" viewBox="0 0 15 15" fill="none"><path d="M3.5 5.5V1.5h8v4M3.5 11h-2v-4.5h12V11h-2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" /><rect x="3.5" y="9.5" width="8" height="4" rx="0.5" stroke="currentColor" strokeWidth="1.3" /><line x1="5.5" y1="11.5" x2="9.5" y2="11.5" stroke="currentColor" strokeWidth="1" /></svg>
                Export PDF
              </button>
            </div>
          </div>

          {view === "detail" && (
            <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, background: `${CORAL}10`, border: `1px solid ${CORAL}25`, borderRadius: 8, padding: "4px 12px" }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: CORAL, boxShadow: `0 0 0 3px ${CORAL}20`, animation: "pulse 2s infinite" }} />
                <span style={{ fontSize: 10, fontWeight: 700, color: CORAL, letterSpacing: "0.05em", textTransform: "uppercase" }}>5-Month Runway</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 7, background: `${NAVY}06`, border: `1px solid ${NAVY}14`, borderRadius: 8, padding: "4px 12px" }}>
                <div style={{ width: 7, height: 7, borderRadius: "50%", background: NAVY, opacity: 0.4 }} />
                <span style={{ fontSize: 10, fontWeight: 600, color: NAVY, letterSpacing: "0.05em", textTransform: "uppercase" }}>12–36 Month Category Development</span>
              </div>
            </div>
          )}
        </div>

        {/* VISUALIZATION */}
        {view === "detail" ? (
          <>
            <div className="print-section" style={{
              ...f1, background: WHITE, borderRadius: 14, border: `1px solid ${BORDER}`,
              padding: "16px 12px 10px", marginTop: 16,
              boxShadow: "0 2px 14px rgba(24,54,94,0.04)",
            }}>
              <DetailTimeline />
            </div>
            <div className="print-section" style={{ ...f2, marginTop: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                <div style={{ width: 4, height: 18, background: CORAL, borderRadius: 2 }} />
                <h2 style={{ fontSize: 13, fontWeight: 700, color: NAVY, margin: 0 }}><span style={{ color: CORAL }}>Growth Vectors</span> — How Scale Compounds</h2>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <VCard icon={ic.pl} label="Product-Led" sub="PSI creates entry" desc="Self-serve, high volume, low friction" phase="Phase 1+" color={CORAL} />
                <VCard icon={ic.ent} label="Enterprise" sub="DVA creates value" desc="High ACV, deep integration, long-term contracts" phase="Phase 1+" color={NAVY} />
                <VCard icon={ic.ch} label="Channel" sub="Consultancies multiply" desc="White-label delivery, partner-driven revenue" phase="Phase 2+" color={NAVY_LIGHT} />
                <VCard icon={ic.dom} label="Domain Expansion" sub="Platform creates scale" desc="Cross-industry, horizontal intelligence" phase="Phase 4+" color={MUTED} />
              </div>
            </div>
          </>
        ) : (
          <div className="print-section" style={{
            ...f1, marginTop: 10,
            borderRadius: 12, border: `1px solid ${BORDER}`,
            boxShadow: "0 3px 20px rgba(24,54,94,0.05)",
            overflow: "hidden",
          }}>
            <SlideTimeline />
          </div>
        )}

        {/* FOOTER */}
        {view === "detail" && (
          <>
            <div style={{ ...f3, display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 18, paddingTop: 10, borderTop: `1px solid ${BORDER}` }}>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                {["Adaptive Product", "Strategy Intelligence Platform", "Confidential"].map((t, i) => (
                  <span key={i}>{i > 0 && <span style={{ fontSize: 10, color: `${MUTED}50`, margin: "0 3px" }}>·</span>}<span style={{ fontSize: 9.5, fontWeight: i === 0 ? 600 : 500, color: MUTED, letterSpacing: "0.05em", textTransform: "uppercase" }}>{t}</span></span>
                ))}
              </div>
              <span style={{ fontSize: 9.5, fontWeight: 500, color: MUTED, fontStyle: "italic" }}>Decision Infrastructure for Health Systems</span>
            </div>
            <div style={{ textAlign: "center", marginTop: 6 }}>
              <span style={{ fontSize: 10, color: MUTED, fontStyle: "italic" }}>"Replace Opinion with Evidence — in the Decisions That Matter Most."</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
